// utils/scopeAttachmentsApi.js
//
// Thin wrapper for the four scope-attachment endpoints on the AutoBot
// Lambda. Every admin screen that shows tenant-scoped resources calls
// through here, so a future change to the wire format is a one-file
// change.
//
// Endpoints:
//
//   POST   /scope_attach
//     body: { app_name, resource_type, tenant_resource_id }
//     → { success: true, attachment_key }
//
//   DELETE /scope_attach?app_name=...&resource_type=...&tenant_resource_id=...
//     → { success: true, attachment_key }
//
//   GET    /scope_attachments?app_name=...&resource_type=...&tenant_resource_id=...
//     → { success: true, count, attachments: [ { app_name, resource_type,
//                                                tenant_resource_id,
//                                                attached_at, attached_by }, ... ] }
//
// All three throw on network / non-2xx / success:false, with the
// server's error message when one is available. Callers surface via
// their existing popup mechanism.
//
// `resource_type` MUST be one of:
//   "api_config" | "chain" | "ui_field_component" | "validation_rules"
//   | "connector_definition" | "oauth_connection"
//
// This set is mirrored from autoBotHandler/src/scope_attach.py.
// Adding a resource type requires changing both sides in one PR.

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const ALLOWED_RESOURCE_TYPES = new Set([
  "api_config",
  "chain",
  "ui_field_component",
  "validation_rules",
  "connector_definition",
  "oauth_connection",
]);

function buildApiBase(region) {
  const regionLower = (region || "us").toLowerCase();
  return `https://${regionLower}.autobot2.${backend_api_base_url}`;
}

function getToken() {
  return localStorage.getItem("token") || "";
}

function assertResourceType(resourceType) {
  if (!resourceType) {
    throw new Error("Missing resource_type");
  }
  if (!ALLOWED_RESOURCE_TYPES.has(resourceType)) {
    throw new Error(
      `Invalid resource_type '${resourceType}'. ` +
        `Allowed: ${Array.from(ALLOWED_RESOURCE_TYPES).sort().join(", ")}.`,
    );
  }
}

function readError(body, resp) {
  return (
    body?.error ||
    body?.detail ||
    body?.message ||
    `Request failed (HTTP ${resp.status})`
  );
}

/**
 * Opt an app into a tenant-scoped resource.
 *
 * Idempotent. Attaching the same (app, resource_type, resource_id)
 * twice is a no-op server-side.
 *
 * Throws on:
 *   • network error
 *   • 400 (missing / invalid fields, or resource_type not tenant-scoped)
 *   • 404 (tenant_resource_id doesn't exist)
 *   • any non-2xx
 */
export async function attachScope({
  region,
  appName,
  resourceType,
  tenantResourceId,
}) {
  if (!appName) throw new Error("Missing appName");
  if (!tenantResourceId) throw new Error("Missing tenantResourceId");
  assertResourceType(resourceType);

  const url = `${buildApiBase(region)}/scope_attach`;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
    body: JSON.stringify({
      app_name: appName,
      resource_type: resourceType,
      tenant_resource_id: tenantResourceId,
    }),
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success) {
    throw new Error(readError(body, resp));
  }

  return body;
}

/**
 * Remove an app's opt-in to a tenant-scoped resource.
 *
 * Idempotent. Detaching a non-existent attachment returns success —
 * the endpoint is designed so a retry after a partial failure always
 * converges to "not attached".
 */
export async function detachScope({
  region,
  appName,
  resourceType,
  tenantResourceId,
}) {
  if (!appName) throw new Error("Missing appName");
  if (!tenantResourceId) throw new Error("Missing tenantResourceId");
  assertResourceType(resourceType);

  const params = new URLSearchParams({
    app_name: appName,
    resource_type: resourceType,
    tenant_resource_id: tenantResourceId,
  });
  const url = `${buildApiBase(region)}/scope_attach?${params.toString()}`;

  const resp = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success) {
    throw new Error(readError(body, resp));
  }

  return body;
}

/**
 * List attachment rows for a tenant, optionally filtered.
 *
 * Filters:
 *   • appName             — bounded to one app's attachments (GSI query)
 *   • resourceType        — bounded to one resource kind
 *   • tenantResourceId    — bounded to one specific tenant resource
 *
 * Without filters, returns every attachment row on the tenant. Rare —
 * only a super-admin diagnostic view would want that.
 */
export async function listAttachments({
  region,
  appName,
  resourceType,
  tenantResourceId,
} = {}) {
  if (resourceType) assertResourceType(resourceType);

  const params = new URLSearchParams();
  if (appName) params.set("app_name", appName);
  if (resourceType) params.set("resource_type", resourceType);
  if (tenantResourceId) params.set("tenant_resource_id", tenantResourceId);

  const qs = params.toString();
  const url = `${buildApiBase(region)}/scope_attachments${qs ? `?${qs}` : ""}`;

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success) {
    throw new Error(readError(body, resp));
  }

  return {
    attachments: Array.isArray(body.attachments) ? body.attachments : [],
    count: typeof body.count === "number" ? body.count : 0,
  };
}

/**
 * Batch-fetch every attachment for a given resource_type on this
 * tenant, grouped by tenant_resource_id.
 *
 * One HTTP call total — used by Group B (global admin) listings to
 * render "Used by" chips on every row without N round trips.
 *
 * The backend endpoint `/scope_attachments?resource_type=X` (no
 * app_name) returns every attachment row of that type on the tenant;
 * the grouping happens here.
 *
 * Returns:
 *   Map<tenantResourceId, {
 *     app_names: string[],   // sorted, deduped
 *     attachments: object[], // full rows, for detail views
 *   }>
 *
 * On failure, returns an empty Map — a listing screen that cannot load
 * used-by data should render "Unknown" chips, not crash.
 */
export async function getAttachmentsByResource({ region, resourceType }) {
  assertResourceType(resourceType);

  const grouped = new Map();

  try {
    const { attachments } = await listAttachments({
      region,
      resourceType,
    });

    for (const row of attachments) {
      const id = row?.tenant_resource_id;
      if (typeof id !== "string" || !id.trim()) continue;

      if (!grouped.has(id)) {
        grouped.set(id, { app_names: [], attachments: [] });
      }
      const bucket = grouped.get(id);
      bucket.attachments.push(row);

      const appName = row?.app_name;
      if (
        typeof appName === "string" &&
        appName.trim() &&
        !bucket.app_names.includes(appName)
      ) {
        bucket.app_names.push(appName);
      }
    }

    for (const bucket of grouped.values()) {
      bucket.app_names.sort();
    }
  } catch (err) {
    // Swallow — the caller renders "Unknown" chips. A transient
    // network failure should not take down the whole listing screen.
    // eslint-disable-next-line no-console
    console.warn(
      "[scopeAttachmentsApi] getAttachmentsByResource failed:",
      err,
    );
    return new Map();
  }

  return grouped;
}

/**
 * Convenience: return the set of tenant_resource_id values that this
 * app has attached for a given resource_type.
 *
 * Returns a Set. On error, returns an empty set — a resolver that
 * can't read the attachment table should behave as "nothing attached",
 * not as a hard failure of the whole listing render.
 */
export async function getAttachedTenantIds({
  region,
  appName,
  resourceType,
}) {
  if (!appName) return new Set();
  try {
    const { attachments } = await listAttachments({
      region,
      appName,
      resourceType,
    });
    return new Set(attachments.map((a) => a.tenant_resource_id));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[scopeAttachmentsApi] getAttachedTenantIds failed:", err);
    return new Set();
  }
}

/**
 * Convenience: return the set of app_names that have attached a given
 * tenant resource.
 *
 * On error, returns an empty set.
 */
export async function getAttachedAppNames({
  region,
  resourceType,
  tenantResourceId,
}) {
  if (!tenantResourceId) return new Set();
  try {
    const { attachments } = await listAttachments({
      region,
      resourceType,
      tenantResourceId,
    });
    return new Set(attachments.map((a) => a.app_name).filter(Boolean));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[scopeAttachmentsApi] getAttachedAppNames failed:", err);
    return new Set();
  }
}

export const SCOPE_RESOURCE_TYPES = Object.freeze({
  API_CONFIG: "api_config",
  CHAIN: "chain",
  UI_FIELD_COMPONENT: "ui_field_component",
  VALIDATION_RULES: "validation_rules",
  CONNECTOR_DEFINITION: "connector_definition",
  OAUTH_CONNECTION: "oauth_connection",
});

export default {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
  getAttachedTenantIds,
  getAttachedAppNames,
  SCOPE_RESOURCE_TYPES,
};